/**
 * Мемоизирующий декоратор (замена `tuiPure` из `@taiga-ui/legacy`, поведение 1:1):
 * - на геттере — значение вычисляется при первом обращении и дальше не пересчитывается;
 * - на методе — результат кэшируется по последним аргументам (сравнение через ===).
 *
 * Кэш хранится на экземпляре. Рассчитан на `experimentalDecorators: true`.
 */
export function pure<T>(
  _target: object,
  propertyKey: string | symbol,
  descriptor: TypedPropertyDescriptor<T>,
): TypedPropertyDescriptor<T> {
  const { get, enumerable, value } = descriptor;

  if (get) {
    return {
      configurable: true,
      enumerable,
      get(this: object): T {
        const result = get.call(this);

        Object.defineProperty(this, propertyKey, { enumerable, value: result });

        return result;
      },
    };
  }

  if (typeof value !== 'function') {
    throw new Error('@pure can only be used with getters and methods');
  }

  const original = value as (...args: unknown[]) => unknown;

  return {
    configurable: true,
    enumerable,
    get(this: object): T {
      let previousArgs: unknown[] = [];
      let calledAtLeastOnce = false;
      let pureValue: unknown;

      const patched = (...args: unknown[]): unknown => {
        const isPure =
          calledAtLeastOnce &&
          previousArgs.length === args.length &&
          args.every((arg, index) => arg === previousArgs[index]);

        if (isPure) {
          return pureValue;
        }

        previousArgs = args;
        pureValue = original.apply(this, args);
        calledAtLeastOnce = true;

        return pureValue;
      };

      Object.defineProperty(this, propertyKey, {
        configurable: true,
        value: patched,
      });

      return patched as T;
    },
  };
}
